import styled from "styled-components";
import {
  GetSongRequests,
  RequestStatuses,
  SongRequest,
  SongRequests,
} from "../../domain/voting";
import React, { useEffect, useRef, useState } from "react";
import { LoadStatus, LoadStatuses } from "../common/loading";
import { LoadingMessagePanel } from "../../components/loadingPanel";
import { LoginDialog } from "./loginDialog";
import { AdminService } from "./adminService";
import { StatusCodes } from "../../services/common";
import { setBackButtonLocation } from "../../components/navPanel";
import { SettingsDialog } from "./settingsDialog";

export const RequestTable = styled.table`
  border: lightgrey 1px solid;
  width: 100%;

  font-size: 2vh;
  table-layout: auto;

  & th {
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    border-left: lightgrey 1px solid;
  }

  & td {
    border: lightgrey 1px solid;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const AdminButton = styled.button`
  font-size: 2vh;
`;

const RequestControls = styled.div`
  display: flex;
  gap: 2vh;
  justify-content: center;
  margin: 1vh 0 0.5vh;
`;

const ConfirmationPanel = styled.div`
  background: white;
  border: lightgrey 1px solid;
  box-sizing: border-box;
  left: 50%;
  max-width: calc(100vw - 4vh);
  padding: 2vh;
  position: fixed;
  text-align: center;
  top: 35%;
  transform: translateX(-50%);
  z-index: 1;
`;

export const VoteRequestRow = ({
  request,
  adminService,
  load,
  controlsDisabled,
}: {
  request: SongRequest;
  adminService: AdminService;
  load: () => Promise<void>;
  controlsDisabled?: boolean;
}) => {
  const action = () => {
    if (request.status == RequestStatuses.PENDING_APPROVAL) {
      return (
        <>
          <td>
            <AdminButton
              disabled={controlsDisabled}
              onClick={async (evt) => {
                try {
                  evt.currentTarget.disabled = true;
                  await adminService.approveSongRequest({
                    requestId: request.requestId,
                    songId: request.song.id,
                    value: request.value,
                  });
                } catch (e) {
                  console.log(e);
                  //TODO: show the toast error
                }
                await load();
              }}
            >
              Approve
            </AdminButton>
          </td>
          <td>
            <AdminButton
              disabled={controlsDisabled}
              onClick={async (evt) => {
                try {
                  evt.currentTarget.disabled = true;
                  await adminService.denySongRequest({
                    requestId: request.requestId,
                    songId: request.song.id,
                  });
                } catch (e) {
                  console.log(e);
                  //TODO: show the toast error
                }
                await load();
              }}
            >
              Deny
            </AdminButton>
          </td>
        </>
      );
    }

    if (request.status == RequestStatuses.APPROVED) {
      return (
        <>
          <td>Approved</td>
          <td>
            <AdminButton
              disabled={controlsDisabled}
              onClick={async (evt) => {
                try {
                  evt.currentTarget.disabled = true;
                  await adminService.archiveSongRequest({
                    requestId: request.requestId,
                    songId: request.song.id,
                  });
                } catch (e) {
                  console.log(e);
                  //TODO: show the toast error
                }
                await load();
              }}
            >
              Archive
            </AdminButton>
          </td>
        </>
      );
    }

    return (
      <td colSpan={2}>
        <div>
          {request.status == RequestStatuses.DENIED ? "Denied" : "Archived"}
        </div>
      </td>
    );
  };

  return (
    <tr aria-label={"dollar-vote-request"}>
      {action()}
      <td aria-label={"amount"}>${request.value}</td>
      <td aria-label={"requested-by"}>{request.requestedBy.name}</td>
      <td aria-label={"title"}>{request.song.title}</td>
      <td aria-label={"timestamp"}>{request.timestamp.toISO()}</td>
      <td aria-label={"request-id"}>{request.requestId}</td>
    </tr>
  );
};

const RequestTableContainer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  width: 100%;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

export const DollarVoteAdminPage = ({
  getSongRequests,
  adminService,
  refreshTime = 10_000,
}: {
  getSongRequests: GetSongRequests;
  adminService: AdminService;
  refreshTime?: number;
}) => {
  const [loadStatus, setLoadStatus] = useState<LoadStatus<SongRequests>>(
    LoadStatuses.UNINITIALIZED,
  );

  const timer = useRef<number | undefined>(undefined);
  const loadRequestId = useRef(0);
  const cancelDeleteButton = useRef<HTMLButtonElement | null>(null);
  const showAllRequestsRef = useRef(false);
  const [showAllRequests, setShowAllRequests] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const load = async (includeAllRequests = showAllRequestsRef.current) => {
    const requestId = ++loadRequestId.current;
    const maybeVoteRequests = await getSongRequests(includeAllRequests);

    if (requestId != loadRequestId.current) {
      return;
    }

    if (maybeVoteRequests.status == StatusCodes.REGISTRATION_REQUIRED) {
      if (timer.current) {
        window.clearInterval(timer.current);
        timer.current = undefined;
      }
      setLoadStatus(LoadStatuses.REGISTRATION_REQUIRED);
    } else if (maybeVoteRequests.status == "OK") {
      setLoadStatus({
        data: maybeVoteRequests.value,
        name: "loaded",
      });
    } else if (
      maybeVoteRequests.status == StatusCodes.INSUFFICIENT_PERMISSIONS
    ) {
      if (timer.current) {
        window.clearInterval(timer.current);
        timer.current = undefined;
      }
      setLoadStatus(LoadStatuses.ADMIN_REQUIRED);
    }
  };

  useEffect(() => {
    setBackButtonLocation("/playlist");

    if (loadStatus.name == LoadStatuses.UNINITIALIZED.name) {
      (async () => {
        setLoadStatus(LoadStatuses.LOADING);
        await load();
      })();
    } else if (loadStatus.name == "loaded" && timer.current == undefined) {
      timer.current = window.setInterval(async () => {
        await load();
      }, refreshTime);
    }
  }, undefined);

  useEffect(() => {
    if (showDeleteConfirmation) {
      cancelDeleteButton.current?.focus();
    }
  }, [showDeleteConfirmation]);

  const requestControls = () => {
    return (
      <RequestControls>
        <AdminButton
          disabled={showDeleteConfirmation}
          onClick={async () => {
            const nextShowAllRequests = !showAllRequests;
            showAllRequestsRef.current = nextShowAllRequests;
            setShowAllRequests(nextShowAllRequests);
            await load(nextShowAllRequests);
          }}
        >
          {showAllRequests
            ? "Hide processed requests"
            : "Show processed requests"}
        </AdminButton>
        <AdminButton
          disabled={showDeleteConfirmation}
          onClick={() => setShowDeleteConfirmation(true)}
        >
          Delete archived/denied requests
        </AdminButton>
        <AdminButton
          disabled={showDeleteConfirmation}
          onClick={() => setShowSettings(true)}
        >
          Settings
        </AdminButton>
      </RequestControls>
    );
  };

  const deleteConfirmation = () => {
    return showDeleteConfirmation ? (
      <ConfirmationPanel
        role={"dialog"}
        aria-label={"delete-processed-requests-confirmation"}
        aria-modal={true}
        onKeyDown={(evt) => {
          if (evt.key == "Escape") {
            setShowDeleteConfirmation(false);
          }
        }}
      >
        <div>
          Delete archived and denied song requests? This cannot be undone.
        </div>
        <RequestControls>
          <AdminButton
            ref={cancelDeleteButton}
            onClick={() => setShowDeleteConfirmation(false)}
          >
            Cancel
          </AdminButton>
          <AdminButton
            onClick={async () => {
              try {
                await adminService.deleteProcessedSongRequests();
                setShowDeleteConfirmation(false);
                await load();
              } catch (e) {
                console.log(e);
                //TODO: show the toast error
              }
            }}
          >
            Confirm delete
          </AdminButton>
        </RequestControls>
      </ConfirmationPanel>
    ) : null;
  };

  //TODO: replace this function with a shared component
  const panel = () => {
    if (loadStatus.name == LoadStatuses.LOADING.name) {
      return <LoadingMessagePanel />;
    } else if (
      loadStatus.name == LoadStatuses.REGISTRATION_REQUIRED.name ||
      loadStatus.name == LoadStatuses.ADMIN_REQUIRED.name
    ) {
      return (
        <LoginDialog
          title={"Admin Login"}
          onSubmit={adminService.login}
          onLogin={async (result) => {
            if (result == "SUCCESS") {
              setLoadStatus(LoadStatuses.UNINITIALIZED);
            }
          }}
        />
      );
    } else if (loadStatus.name == "loaded") {
      if (loadStatus.data?.page) {
        return (
          <>
            <PageContent aria-hidden={showDeleteConfirmation}>
              {loadStatus.data.page.length == 0 ? (
                <EmptyPanel />
              ) : (
                <RequestTableContainer className={"request-table-container"}>
                  <RequestTable aria-label={"dollar-vote-request-table"}>
                    <thead role={"columnheader"}>
                      <tr>
                        <th colSpan={2}>Actions</th>
                        <th>Amount</th>
                        <th>Requested by</th>
                        <th>Title</th>
                        <th>Timestamp</th>
                        <th>Request Id</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadStatus.data.page.map((sr) => (
                        <VoteRequestRow
                          key={sr.requestId}
                          request={sr}
                          adminService={adminService}
                          load={load}
                          controlsDisabled={showDeleteConfirmation}
                        ></VoteRequestRow>
                      ))}
                    </tbody>
                  </RequestTable>
                </RequestTableContainer>
              )}
              {requestControls()}
            </PageContent>
            {deleteConfirmation()}
            {showSettings && (
              <SettingsDialog
                adminService={adminService}
                onClose={() => setShowSettings(false)}
              />
            )}
          </>
        );
      } else {
        // TODO: can we eliminate this case with type manipulations?
        console.error("This isn't supposed to happen");
        return <EmptyPanel />;
      }
    }
  };

  return <Container>{panel()}</Container>;
};

const Container = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  height: 100%;
  padding: 1vh;
  box-sizing: border-box;
`;

const EmptyPanel = () => {
  return (
    <>
      <div role={"note"} aria-label={"empty-playlist"} className="message">
        Nothing to display
      </div>
    </>
  );
};
